import React, { useMemo } from 'react';
import { useWatch, Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { PayoutsForm } from '@/modules/onboarding/model/payoutsStep';
import { USPartnerType } from '@/modules/partner/model/partnerType';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

import { messages } from './usMessages';

type Props = {
  control: Control<PayoutsForm>;
};

const ssnRegex = /^\d{9}$/;
const einRegex = /^\d{9}$/;

const UsPaymentType: React.FC<Props> = ({ control }) => {
  const intl = useIntl();

  const partnerTypes = useMemo(
    () => [
      {
        title: intl.formatMessage(messages.payMyselfOptionTitle),
        value: USPartnerType.selfEmployed,
      },
      {
        title: intl.formatMessage(messages.payMyCompanyOptionTitle),
        value: USPartnerType.company,
      },
    ],
    [intl],
  );

  const selectedPartnerType = useWatch({ control, name: 'partnerType' });

  const isPayMyself = selectedPartnerType === USPartnerType.selfEmployed;
  const isCompany = selectedPartnerType === USPartnerType.company;

  return (
    <>
      <SelectField
        control={control}
        name="partnerType"
        title={intl.formatMessage(messages.payoutTypeTitle)}
        items={partnerTypes}
      />

      {isPayMyself && (
        <TextField
          shouldUnregister={true}
          name="ssn"
          control={control}
          isRequired={true}
          title={intl.formatMessage(messages.ssnTitle)}
          placeholder="123456789"
          rules={{
            pattern: {
              value: ssnRegex,
              message: intl.formatMessage(messages.ssnError),
            },
          }}
        />
      )}

      {isCompany && (
        <>
          <TextField
            shouldUnregister={true}
            name="companyName"
            control={control}
            isRequired={true}
            title={intl.formatMessage(messages.legalNameTitle)}
          />

          <TextField
            shouldUnregister={true}
            name="dbaName"
            control={control}
            title={intl.formatMessage(messages.dbaTitle)}
          />

          <TextField
            shouldUnregister={true}
            name="companyAddress"
            control={control}
            isRequired={true}
            title={intl.formatMessage(messages.legalAddressTitle)}
          />

          <TextField
            shouldUnregister={true}
            name="zipCode"
            control={control}
            isRequired={true}
            title={intl.formatMessage(messages.zipCodeTitle)}
          />

          <TextField
            shouldUnregister={true}
            name="city"
            control={control}
            isRequired={true}
            title={intl.formatMessage(messages.cityTitle)}
          />

          <TextField
            shouldUnregister={true}
            name="licenseNumber"
            control={control}
            title={intl.formatMessage(messages.tlcBaseAffiliationTitle)}
          />

          <TextField
            shouldUnregister={true}
            name="ein"
            control={control}
            isRequired={true}
            title={intl.formatMessage(messages.einTitle)}
            placeholder="123456789"
            rules={{
              pattern: {
                value: einRegex,
                message: intl.formatMessage(messages.einError),
              },
            }}
          />
        </>
      )}
    </>
  );
};

export { UsPaymentType };
